import type { NpsDialogProps } from './components/nps/NpsDialog';
import type * as lazyIndex from './lazyIndex';
import { LazyModule, LazyAction, createLazyComponent, type LazyComponent } from 'owa-bundling';
import type { UpvoteFeedbackProps } from './utils/upvoteFeedbackProps';
import type { FeedbackDialogProps } from './utils/feedbackDialogProps';

const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule<typeof lazyIndex>(
    () => import(/* webpackChunkName: "DiagnosticsAndSupport" */ './lazyIndex'),
    { name: 'DiagnosticsAndSupport' }
);

// Delay loaded components for in-app support
export const lazyHandleSupportPaneEvents: LazyAction<
    typeof lazyIndex.handleSupportPaneEvents,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.handleSupportPaneEvents);
export const lazyLaunchLegacyAppAndMinimizeMonarch: LazyAction<
    typeof lazyIndex.launchLegacyAppAndMinimizeMonarch,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.launchLegacyAppAndMinimizeMonarch);
export const lazyLaunchOutlookDesktopSideBySideOnce: LazyAction<
    typeof lazyIndex.launchOutlookDesktopSideBySideOnce,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.launchOutlookDesktopSideBySideOnce);
export const lazyToggleOffToLegacyApp: LazyAction<
    typeof lazyIndex.toggleOffToLegacyApp,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.toggleOffToLegacyApp);
export const lazyInitializeSupportOnBoot: LazyAction<
    typeof lazyIndex.initializeSupportOnBoot,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.initializeSupportOnBoot);
export const lazyRenderSupportPane: LazyAction<
    typeof lazyIndex.renderSupportPane,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.renderSupportPane);
export const lazyOnSupportPaneClose: LazyAction<
    typeof lazyIndex.onSupportPaneClose,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.onSupportPaneClose);

export const lazyRenderFeedbackPane: LazyAction<
    typeof lazyIndex.renderFeedbackPane,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.renderFeedbackPane);
export const lazyLaunchInContextFeedback: LazyAction<
    typeof lazyIndex.launchInContextFeedback,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.launchInContextFeedback);

export const lazySendFeedbackMessage: LazyAction<
    typeof lazyIndex.sendFeedbackMessage,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.sendFeedbackMessage);

export const lazyRenderGetDiagnosticsPane: LazyAction<
    typeof lazyIndex.renderGetDiagnosticsPane,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.renderGetDiagnosticsPane);

export const lazyLaunchInContextSupport: LazyAction<
    typeof lazyIndex.launchInContextSupport,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.launchInContextSupport);

export const lazyLaunchSupportInContainer: LazyAction<
    typeof lazyIndex.launchSupportInContainer,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.launchSupportInContainer);
export const lazySendMicroFeedback: LazyAction<
    typeof lazyIndex.sendMicroFeedback,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.sendMicroFeedback);

export const FeedbackDialog: LazyComponent<FeedbackDialogProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.FeedbackDialog);

export const UpvoteFeedback: LazyComponent<UpvoteFeedbackProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.UpvoteFeedback);

export const NpsDialog: LazyComponent<NpsDialogProps, unknown, void> = createLazyComponent(
    lazyModule,
    m => m.NpsDialog
);

export const lazyGetIframeUrl: LazyAction<typeof lazyIndex.getIframeUrl, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.getIframeUrl);

export const lazySetClosePane: LazyAction<typeof lazyIndex.setClosePane, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.setClosePane);

export const lazySetSwitchToTeamsCalendar: LazyAction<
    typeof lazyIndex.setSwitchToTeamsCalendar,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setSwitchToTeamsCalendar);

export const lazyLoadNpsCopilotView: LazyAction<
    typeof lazyIndex.loadNpsCopilotView,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.loadNpsCopilotView);

export const lazyRenderOcvFeedbackPanel: LazyAction<
    typeof lazyIndex.renderOcvFeedbackPanel,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.renderOcvFeedbackPanel);

export const lazyIsSideBySideOnceEnabled: LazyAction<
    typeof lazyIndex.isSideBySideOnceEnabled,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.isSideBySideOnceEnabled);

export const lazyGetUserSettingsMigrationState: LazyAction<
    typeof lazyIndex.getUserSettingsMigrationState,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.getUserSettingsMigrationState);

// Non-lazy util functions
export { isSupportEnabled } from './utils/utils';

// Constants
export {
    bookabletimeAppName,
    bookingsAppName,
    bookingsMobileFeedbackScenario,
    entrypointStringActiveWin32User,
    eventifyAppName,
    feedbackScenarioName,
    metaOSCalendarAppName,
    monarchTeamsAppName,
    monarchTeamsToggleFeedbackScenario,
    placesAppName,
} from './exportedConstants';
